import { Box, Typography } from "@mui/material";
import React from "react";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { slidingPageColumnSx } from "../styles/mainStyles";

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';

/** Full-viewport shell (matches Library / Settings layout). */
function groupedScreenBleedSx() {
    return {
        bgcolor: "transparent",
        background: "transparent",
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        minHeight: "100%",
        boxSizing: "border-box",
    };
}

/** Horizontal inset so grouped white cards align with hero / library content. */
const groupedContentInsetSx = {
    ...slidingPageColumnSx,
    pt: 0.5,
    pb: 2,
};

/**
 * iOS `GroupedListStyle` section wrapper (Screen 7).
 */
export function GroupedList({ children, sx }) {
    return (
        <Box sx={{ ...groupedScreenBleedSx(), ...sx }}>
            <Box sx={groupedContentInsetSx}>{children}</Box>
        </Box>
    );
}

export function GroupedSection({ title, footer, children, sx }) {
    const { tokens } = useSlidingAppearance();

    return (
        <Box component="section" sx={{ mb: 2.5, ...sx }}>
            {title ? (
                <Typography
                    component="h2"
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "0.8125rem",
                        fontWeight: 400,
                        color: tokens.sectionHeader,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        mb: 0.75,
                    }}
                >
                    {title}
                </Typography>
            ) : null}
            <Box
                sx={{
                    bgcolor: tokens.cardBg,
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: tokens.elevatedCardBorder,
                    boxShadow: tokens.cardShadow,
                }}
            >
                {children}
            </Box>
            {footer ? (
                <Typography
                    component="p"
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "0.8125rem",
                        lineHeight: 1.45,
                        color: tokens.sectionHeader,
                        pt: 1,
                        m: 0,
                    }}
                >
                    {footer}
                </Typography>
            ) : null}
        </Box>
    );
}

/**
 * Row inside a grouped section; adds inset divider unless `last`.
 */
export function GroupedRow({ children, last = false, sx }) {
    const { tokens } = useSlidingAppearance();

    return (
        <Box
            sx={{
                px: 2,
                py: 1.75,
                borderBottom: last ? "none" : `0.5px solid ${tokens.divider}`,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

export { systemFont };
